import React from "react";
import { motion } from "framer-motion";
import { CheckCircle, Warning, Plant, ThermometerHot, Lightning, ArrowRight } from "@phosphor-icons/react";

const severityClass = {
  none: "severity-none",
  mild: "severity-mild",
  moderate: "severity-moderate",
  severe: "severity-severe",
  critical: "severity-critical",
};

function Section({ title, children, testid }) {
  return (
    <div className="bg-white/70 border border-sand-200 rounded-2xl p-5" data-testid={testid}>
      <div className="font-mono-data text-mute mb-3">{title}</div>
      {children}
    </div>
  );
}

// Normalize the per-kind GPT output shape into a single shape the UI can render.
function normalize(analysis, kind = "plant") {
  if (!analysis) return null;
  const climate_contribution = analysis.climate_contribution || {};

  if (kind === "soil") {
    const id = analysis.soil_identification || {};
    const pa = analysis.primary_assessment || {};
    const ind = analysis.indicators || {};
    const indicatorList = Object.entries(ind)
      .map(([k, v]) => {
        if (typeof v === "boolean") return v ? k.replace(/_/g, " ") : null;
        if (v && v !== "unknown") return `${k.replace(/_/g, " ")}: ${v}`;
        return null;
      })
      .filter(Boolean);
    return {
      kindLabel: "Soil",
      ident: { common_name: id.soil_type_guess || "Soil sample", scientific_name: id.texture_guess && id.texture_guess !== "unknown" ? `${id.texture_guess} texture` : null },
      primary: { disorder: pa.condition, category: pa.category, severity: pa.severity, confidence: pa.confidence },
      summary: analysis.summary,
      climate_contribution,
      symptomsTitle: "Indicators",
      symptoms: indicatorList,
      causesTitle: "Suitable crops",
      causes: analysis.suitable_crops || [],
      actions: analysis.improvement_actions || [],
      tipsTitle: "Amendments",
      tips: (analysis.amendments || []).map((a) => (typeof a === "string" ? a : `${a.name || ""}${a.reason ? " — " + a.reason : ""}`)),
      differentials: [],
    };
  }

  if (kind === "fruit") {
    const id = analysis.fruit_identification || {};
    const pd = analysis.primary_diagnosis || {};
    const ripe = analysis.ripeness || {};
    const qual = analysis.quality_assessment || {};
    const extras = [];
    if (ripe.stage) extras.push(`Ripeness: ${ripe.stage} → ${ripe.harvest_recommendation || "—"}`);
    if (typeof ripe.estimated_days_to_ripe === "number" && ripe.estimated_days_to_ripe > 0) extras.push(`~${ripe.estimated_days_to_ripe}d to ripe`);
    if (qual.grade) extras.push(`Grade: ${qual.grade}`);
    if (qual.shelf_life_days_estimate) extras.push(`Shelf life ~${qual.shelf_life_days_estimate}d`);
    return {
      kindLabel: "Fruit",
      ident: { common_name: id.common_name || "Fruit", scientific_name: id.scientific_name || id.variety_guess },
      primary: { disorder: pd.issue, category: pd.category, severity: pd.severity, confidence: pd.confidence },
      summary: analysis.summary,
      climate_contribution,
      symptomsTitle: "Visible defects",
      symptoms: analysis.visible_defects || [],
      causesTitle: "Ripeness & quality",
      causes: extras,
      actions: analysis.recommended_actions || [],
      tipsTitle: "Storage tips",
      tips: analysis.storage_tips || [],
      differentials: (analysis.differential_diagnoses || []).map((d) => ({ disorder: d.issue || d.disorder, likelihood: d.likelihood })),
    };
  }

  if (kind === "seed") {
    const id = analysis.seed_identification || {};
    const pd = analysis.primary_diagnosis || {};
    const via = analysis.viability_assessment || {};
    const qi = analysis.quality_indicators || {};
    const ic = analysis.ideal_conditions || {};
    const symptoms = Object.entries(qi)
      .map(([k, v]) => {
        if (typeof v === "boolean") return v ? k.replace(/_/g, " ") : null;
        if (v && v !== "unknown") return `${k.replace(/_/g, " ")}: ${v}`;
        return null;
      })
      .filter(Boolean);
    const causes = [];
    if (via.viability) causes.push(`Viability: ${via.viability} (${via.germination_probability_pct || 0}% germ)`);
    if (via.estimated_days_to_germinate) causes.push(`~${via.estimated_days_to_germinate}d to germinate`);
    if (ic.soil_temp_c_min || ic.soil_temp_c_max) causes.push(`Soil temp: ${ic.soil_temp_c_min || 0}–${ic.soil_temp_c_max || 0} °C`);
    if (ic.depth_cm) causes.push(`Sow depth: ${ic.depth_cm} cm`);
    if (ic.spacing_cm) causes.push(`Spacing: ${ic.spacing_cm} cm`);
    if (ic.light_needs && ic.light_needs !== "unknown") causes.push(`Light: ${ic.light_needs}`);
    return {
      kindLabel: "Seed",
      ident: { common_name: id.common_name || "Seed", scientific_name: id.scientific_name || id.variety_guess },
      primary: { disorder: pd.issue, category: pd.category, severity: pd.severity, confidence: pd.confidence },
      summary: analysis.summary,
      climate_contribution,
      symptomsTitle: "Quality indicators",
      symptoms,
      causesTitle: "Viability & ideal conditions",
      causes,
      actions: analysis.sowing_recommendations || [],
      tipsTitle: "Storage tips",
      tips: analysis.storage_tips || [],
      differentials: (analysis.differential_diagnoses || []).map((d) => ({ disorder: d.issue || d.disorder, likelihood: d.likelihood })),
    };
  }

  if (kind === "flower") {
    const id = analysis.flower_identification || {};
    const pd = analysis.primary_diagnosis || {};
    const bloom = analysis.bloom_stage || {};
    const poll = analysis.pollination_potential || {};
    const extras = [];
    if (bloom.stage) extras.push(`Bloom stage: ${bloom.stage}`);
    if (typeof bloom.estimated_days_remaining === "number" && bloom.estimated_days_remaining > 0) extras.push(`~${bloom.estimated_days_remaining}d remaining`);
    if (poll.rating) extras.push(`Pollination: ${poll.rating}`);
    return {
      kindLabel: "Flower",
      ident: { common_name: id.common_name || "Flower", scientific_name: id.scientific_name || id.cultivar_guess },
      primary: { disorder: pd.disorder, category: pd.category, severity: pd.severity, confidence: pd.confidence },
      summary: analysis.summary,
      climate_contribution,
      symptomsTitle: "Visible symptoms",
      symptoms: analysis.visible_symptoms || [],
      causesTitle: "Bloom & pollination",
      causes: extras,
      actions: analysis.recommended_actions || [],
      tipsTitle: "Care tips",
      tips: analysis.care_tips || [],
      differentials: analysis.differential_diagnoses || [],
    };
  }

  // plant (default)
  const ident = analysis.plant_identification || {};
  const p = analysis.primary_diagnosis || {};
  return {
    kindLabel: "Plant",
    ident: { common_name: ident.common_name, scientific_name: ident.scientific_name },
    primary: p,
    summary: analysis.summary,
    climate_contribution,
    symptomsTitle: "Visible symptoms",
    symptoms: analysis.visible_symptoms || [],
    causesTitle: "Likely causes",
    causes: analysis.likely_causes || [],
    actions: analysis.recommended_actions || [],
    tipsTitle: "Prevention",
    tips: analysis.prevention_tips || [],
    differentials: analysis.differential_diagnoses || [],
  };
}

export default function DiagnosisCard({ analysis, climate, kind = "plant" }) {
  const data = normalize(analysis, kind);
  if (!data) return null;
  const p = data.primary || {};
  const ident = data.ident || {};
  const climateInfo = data.climate_contribution || {};
  const isHealthy = (p.category || "").toLowerCase() === "healthy";
  const sev = (p.severity || "mild").toLowerCase();

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-5"
      data-testid="diagnosis-card"
    >
      <div className="bg-sand-100 border border-sand-200 rounded-3xl p-6 relative overflow-hidden">
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-forest/10 blur-3xl" />
        <div className="flex items-start justify-between gap-4 relative">
          <div>
            <div className="font-mono-data text-mute mb-2">Primary diagnosis</div>
            <h2 className="font-display text-3xl md:text-4xl text-ink leading-tight" data-testid="diagnosis-name">
              {p.disorder || "Inconclusive"}
            </h2>
            <div className="flex flex-wrap gap-2 mt-3">
              <span className={`tag ${severityClass[sev] || "severity-mild"}`} data-testid="diagnosis-severity">
                {isHealthy ? <CheckCircle size={12} weight="fill" className="mr-1" /> : <Warning size={12} weight="fill" className="mr-1" />}
                {p.severity || "—"}
              </span>
              <span className="tag" data-testid="diagnosis-category">{p.category || "—"}</span>
              <span className="tag" data-testid="diagnosis-confidence">conf {(p.confidence ?? 0).toFixed(2)}</span>
            </div>
          </div>
          <div className="text-right">
            <div className="font-mono-data text-mute">{data.kindLabel}</div>
            <div className="flex items-center gap-2 justify-end mt-1">
              <Plant size={20} weight="duotone" className="text-forest" />
              <span className="font-display text-xl text-ink">{ident.common_name || "Unknown"}</span>
            </div>
            {ident.scientific_name && (
              <div className="italic text-sm text-mute">{ident.scientific_name}</div>
            )}
          </div>
        </div>

        {data.summary && (
          <p className="mt-5 text-ink/90 leading-relaxed" data-testid="diagnosis-summary">
            {data.summary}
          </p>
        )}
      </div>

      {climateInfo && (
        <Section title="Climate contribution" testid="climate-contribution-section">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-ochre/20 flex items-center justify-center text-ochre-dark">
              <ThermometerHot size={24} weight="duotone" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div className="font-display text-2xl text-ink" data-testid="climate-risk-score">
                  Climate risk: <span className="text-forest">{climateInfo.climate_risk_score ?? 0}</span>
                  <span className="text-mute text-base">/100</span>
                </div>
                {climateInfo.is_climate_driven && (
                  <span className="tag severity-moderate">climate-driven</span>
                )}
              </div>
              <div className="h-2 bg-sand-200 rounded-full mt-2 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-forest to-ochre"
                  style={{ width: `${Math.max(0, Math.min(100, climateInfo.climate_risk_score ?? 0))}%` }}
                />
              </div>
              {climateInfo.explanation && (
                <p className="text-sm text-mute mt-3" data-testid="climate-explanation">{climateInfo.explanation}</p>
              )}
              {Array.isArray(climateInfo.stressors) && climateInfo.stressors.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {climateInfo.stressors.map((s, i) => (
                    <span key={i} className="tag severity-mild">{String(s).replace(/_/g, " ")}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Section>
      )}

      <div className="grid md:grid-cols-2 gap-5">
        {Array.isArray(data.symptoms) && data.symptoms.length > 0 && (
          <Section title={data.symptomsTitle} testid="symptoms-section">
            <ul className="space-y-2">
              {data.symptoms.map((s, i) => (
                <li key={i} className="flex items-start gap-2 text-ink/90">
                  <Warning size={16} weight="duotone" className="text-terra mt-1 shrink-0" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}
        {Array.isArray(data.causes) && data.causes.length > 0 && (
          <Section title={data.causesTitle} testid="causes-section">
            <ul className="space-y-2">
              {data.causes.map((c, i) => (
                <li key={i} className="flex items-start gap-2 text-ink/90">
                  <Lightning size={16} weight="duotone" className="text-ochre-dark mt-1 shrink-0" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}
      </div>

      {Array.isArray(data.actions) && data.actions.length > 0 && (
        <Section title="Recommended actions" testid="actions-section">
          <div className="space-y-3">
            {data.actions.map((a, i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-sand-50 border border-sand-200">
                <div className="w-8 h-8 rounded-full bg-forest text-white flex items-center justify-center shrink-0">
                  <ArrowRight size={14} weight="bold" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-ink">{a.action}</span>
                    {a.priority && <span className="tag severity-moderate">{a.priority.replace(/_/g, " ")}</span>}
                  </div>
                  {a.detail && <p className="text-sm text-mute mt-1">{a.detail}</p>}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {Array.isArray(data.tips) && data.tips.length > 0 && (
        <Section title={data.tipsTitle} testid="prevention-section">
          <ul className="space-y-2">
            {data.tips.map((t, i) => (
              <li key={i} className="flex items-start gap-2 text-ink/90">
                <CheckCircle size={16} weight="duotone" className="text-forest mt-1 shrink-0" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {Array.isArray(data.differentials) && data.differentials.length > 0 && (
        <Section title="Other possibilities" testid="differentials-section">
          <div className="grid sm:grid-cols-2 gap-2">
            {data.differentials.map((d, i) => (
              <div key={i} className="bg-sand-50 border border-sand-200 rounded-xl p-3 flex items-center justify-between">
                <span className="text-ink">{d.disorder}</span>
                <span className="font-mono-data text-mute">{((d.likelihood ?? 0) * 100).toFixed(0)}%</span>
              </div>
            ))}
          </div>
        </Section>
      )}
    </motion.div>
  );
}
